import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  JournalEntryFilterParams,
  JournalEntryLineRecord,
  JournalEntryRecord,
  SaveJournalEntryRequest,
} from '../types/accounting';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

const mapJournalEntry = (
  value: Record<string, unknown>,
): JournalEntryRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  posting_scope: value.posting_scope as JournalEntryRecord['posting_scope'],
  reference_type: (value.reference_type as string | null) ?? null,
  reference_id: (value.reference_id as string | null) ?? null,
  source_module: value.source_module as JournalEntryRecord['source_module'],
  entry_date: String(value.entry_date || ''),
  memo: (value.memo as string | null) ?? null,
  display_memo: (value.display_memo as string | null) ?? null,
  related_bill_number: (value.related_bill_number as string | null) ?? null,
  status: value.status as JournalEntryRecord['status'],
  reversal_of_journal_entry_id:
    (value.reversal_of_journal_entry_id as string | null) ?? null,
  reversed_by_journal_entry_id:
    (value.reversed_by_journal_entry_id as string | null) ?? null,
  reversed_at: (value.reversed_at as string | null) ?? null,
  reversed_by: (value.reversed_by as string | null) ?? null,
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

const mapJournalEntryLine = (
  value: Record<string, unknown>,
): JournalEntryLineRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  journal_entry_id: String(value.journal_entry_id || ''),
  account_id: String(value.account_id || ''),
  outlet_id: (value.outlet_id as string | null) ?? null,
  debit: toNumber(value.debit),
  credit: toNumber(value.credit),
  description: (value.description as string | null) ?? null,
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
});

export const fetchJournalEntries = async ({
  companyId,
  outletId,
  status = 'all',
  sourceModule = 'all',
  search,
  dateFrom,
  dateTo,
  exportAll = false,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: JournalEntryFilterParams): Promise<
  ApiResponse<PaginatedResponse<JournalEntryRecord>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('journal_entries_view')
      .select('*', { count: 'exact' })
      .eq('company_id', companyId)
      .order('entry_date', { ascending: false })
      .order('created_at', { ascending: false });

    if (!exportAll) {
      query = query.range(from, to);
    }

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    if (status !== 'all') {
      query = query.eq('status', status);
    }

    if (sourceModule !== 'all') {
      query = query.eq('source_module', sourceModule);
    }

    if (dateFrom) {
      query = query.gte('entry_date', dateFrom);
    }

    if (dateTo) {
      query = query.lte('entry_date', dateTo);
    }

    if (search?.trim()) {
      const trimmedSearch = search.trim();
      query = query.or(
        `memo.ilike.%${trimmedSearch}%,reference_type.ilike.%${trimmedSearch}%,reference_id.ilike.%${trimmedSearch}%,related_bill_number.ilike.%${trimmedSearch}%`,
      );
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: (data || []).map((item) => mapJournalEntry(item)),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch journal entries',
      data: undefined,
    };
  }
};

export const fetchJournalEntryById = async (
  companyId: number,
  journalEntryId: string,
): Promise<
  ApiResponse<{
    entry: JournalEntryRecord | null;
    lines: JournalEntryLineRecord[];
  }>
> => {
  try {
    const [
      { data: entryData, error: entryError },
      { data: linesData, error: linesError },
    ] = await Promise.all([
      supabase
        .from('journal_entries_view')
        .select('*')
        .eq('company_id', companyId)
        .eq('id', journalEntryId)
        .maybeSingle(),
      supabase
        .from('journal_entry_lines')
        .select('*')
        .eq('company_id', companyId)
        .eq('journal_entry_id', journalEntryId)
        .order('created_at', { ascending: true }),
    ]);

    if (entryError) {
      return {
        success: false,
        error: entryError.message,
        data: { entry: null, lines: [] },
      };
    }

    if (linesError) {
      return {
        success: false,
        error: linesError.message,
        data: {
          entry: entryData ? mapJournalEntry(entryData) : null,
          lines: [],
        },
      };
    }

    return {
      success: true,
      data: {
        entry: entryData ? mapJournalEntry(entryData) : null,
        lines: (linesData || []).map((item) => mapJournalEntryLine(item)),
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch journal entry',
      data: { entry: null, lines: [] },
    };
  }
};

export const saveDraftJournalEntry = async (
  input: SaveJournalEntryRequest,
): Promise<ApiResponse<JournalEntryRecord>> => {
  const payload = {
    company_id: input.companyId,
    outlet_id: input.outletId ?? null,
    posting_scope: input.postingScope,
    reference_type: input.referenceType?.trim() || null,
    reference_id: input.referenceId ?? null,
    source_module: input.sourceModule,
    entry_date: input.entryDate,
    memo: input.memo?.trim() || null,
    status: 'draft' as const,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('journal_entries')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('id', input.id)
        .eq('status', 'draft')
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapJournalEntry(data) };
    }

    const { data, error } = await supabase
      .from('journal_entries')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapJournalEntry(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save draft journal entry',
      data: undefined,
    };
  }
};

export const deleteDraftJournalEntry = async (
  companyId: number,
  journalEntryId: string,
): Promise<ApiResponse<null>> => {
  try {
    const { error } = await supabase
      .from('journal_entries')
      .delete()
      .eq('company_id', companyId)
      .eq('id', journalEntryId)
      .eq('status', 'draft');

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: null };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to delete draft journal entry',
      data: null,
    };
  }
};

export const postJournalEntry = async (
  companyId: number,
  journalEntryId: string,
): Promise<ApiResponse<JournalEntryRecord>> => {
  try {
    const { data, error } = await supabase
      .from('journal_entries')
      .update({ status: 'posted' })
      .eq('company_id', companyId)
      .eq('id', journalEntryId)
      .eq('status', 'draft')
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapJournalEntry(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to post journal entry',
      data: undefined,
    };
  }
};

export const reverseJournalEntry = async (
  companyId: number,
  journalEntryId: string,
  reversalDate?: string,
  memo?: string | null,
): Promise<ApiResponse<JournalEntryRecord>> => {
  try {
    const { data, error } = await supabase.rpc('reverse_journal_entry', {
      p_company_id: companyId,
      p_journal_entry_id: journalEntryId,
      p_reversal_date: reversalDate ?? null,
      p_memo: memo?.trim() || null,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapJournalEntry(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to reverse journal entry',
      data: undefined,
    };
  }
};
